/**
 * SessionExpiredModal - Displays when user session expires
 *
 * Shows a blocking modal that requires user to click "Sign in again".
 * User's current page remains visible behind the modal overlay.
 * Does NOT show on auth pages (login, oauth callback).
 */

import React, { useEffect } from "react";
import { useAtom, useSetAtom } from "jotai";
import { useNavigate, useLocation } from "react-router-dom";
import { sessionExpiredAtom, clearAuthStateAtom } from "../atoms/authAtoms";
import { FaExclamationTriangle } from "../utils/icons";
import { authService } from "../services/auth";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/design-system/components/react";

// Public credential pages must be able to finish or recover a sign-in without
// being obscured by an expiry modal left over from the protected route that
// brought the user here (#892).
const AUTH_PAGES = [
  "/login",
  "/oauth/callback",
  "/set-password",
  "/forgot-password",
  "/reset-password",
];

export const SessionExpiredModal: React.FC = () => {
  const [isExpired, setIsExpired] = useAtom(sessionExpiredAtom);
  const clearAuth = useSetAtom(clearAuthStateAtom);
  const navigate = useNavigate();
  const location = useLocation();

  // Clear expired state when on auth pages
  const isOnAuthPage = AUTH_PAGES.some((page) => location.pathname.startsWith(page));

  useEffect(() => {
    if (isOnAuthPage && isExpired) {
      setIsExpired(false);
    }
  }, [isOnAuthPage, isExpired, setIsExpired]);

  const handleSignIn = async () => {
    // Clear auth state
    await authService.logout();
    clearAuth();
    setIsExpired(false);
    // Navigate to login, carrying the page they were on so Login returns them
    // there after signing in. Same `state.from` shape PrivateRoute uses — the
    // modal is the one bounce-to-login path that isn't a route guard, and
    // without this every expiry-driven re-login dumps the user on /worklist.
    navigate("/login", { state: { from: location } });
  };

  // Don't show modal on auth pages
  if (isOnAuthPage) {
    return null;
  }

  return (
    <AlertDialog open={isExpired}>
      <AlertDialogContent className="bg-bg-2 border-border-strong">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-fg flex items-center gap-3">
            <div className="p-2 bg-warn-tint rounded-full">
              <FaExclamationTriangle className="w-5 h-5 text-warn" />
            </div>
            Session Expired
          </AlertDialogTitle>
          <AlertDialogDescription className="text-fg-muted">
            Your session has expired. Please sign in again to continue.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleSignIn} className="w-full sm:w-auto">
            Sign in again
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SessionExpiredModal;
